import { reactive, ref } from 'vue'
import { useUserStore } from '@/store/userStore'
import { getUserFromToken } from '@/services/auth'
import type { User } from '@/types'
import { UserRoles } from '@/shared/models/UserRoles'
import { storeRegistry } from '@/store/storeRegistry'

const user = reactive<User>({
  id: '',
  first_name: '',
  last_name: '',
  email: '',
  tenant_id: '',
  tenant_name: '',
  company_name: '',
  roles: []
})

let initPromise: Promise<void> | null = null
const initialized = ref(false)

export function useUser() {
  const userStore = useUserStore()

  async function initialize(): Promise<void> {
    if (initialized.value) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      const tokenUser = await getUserFromToken()
      if (tokenUser) {
        Object.assign(user, tokenUser)
        try {
          await userStore.fetchUserDetails(user)
          user.company_name = userStore.extraUserData.companyName
          user.tenant_name = userStore.extraUserData.tenantName
        } catch {
          // fetchUserDetails handles its own error toasts;
          // mark initialized so we don't retry and show duplicate toasts
        }
        initialized.value = true
      } else {
        // no token -> remain uninitialized (caller can handle)
      }
    })()

    try {
      await initPromise
    } finally {
      initPromise = null
    }
  }

  function is_global_admin(): boolean {
    return !!user.roles?.some(r => r.includes(UserRoles.PlatformDeveloper) || r.includes(UserRoles.PlatformAdmin))
  }

  function is_admin(): boolean {
    return !!(
      user.roles?.some(r => r.includes(UserRoles.PlatformDeveloper) || r.includes(UserRoles.PlatformAdmin)) ||
      user.roles?.includes(UserRoles.TenantAdmin)
    )
  }

  return {
    user,
    extraData: userStore.extraUserData,
    is_global_admin,
    is_admin,
    initialize,
    userSettings: userStore.userSettings,
    initialized
  }
}


export function resetUserState() {
  Object.assign(user, {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    tenant_id: '',
    tenant_name: '',
    company_name: '',
    roles: []
  })
  initialized.value = false
  initPromise = null
}

storeRegistry.register('userState', resetUserState)
